import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import AppModal from "components/ui/AppModal";
import AppSimpleText from "components/ui/AppSimpleText.jsx";
import AppInput from "components/ui/AppInput.jsx";
import AppButton from "components/ui/AppButton.jsx";
import { UTILS } from "utils";
import {
  useCreateUserWalletTransactionMutation,
  useGetUserWalletBalanceMutation,
} from "redux/loyalty/loyalty-service";

/**
 * Add manual loyalty points for a customer (wallet credit).
 * @param {boolean} open
 * @param {() => void} onClose
 * @param {{ _id: string, name?: string, phoneNumber?: string, countryCode?: string, lifetimeSpending?: number }} customer
 * @param {{ balance?: number, lifetimeSpending?: number } | null} [initialBalance] — when set, used instead of fetching (e.g. wallet detail page)
 * @param {() => void | Promise<void>} [onSuccess] — refresh list / history after success
 */
const CreditLoyaltyPointsModal = ({
  open,
  onClose,
  customer,
  initialBalance = null,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useSelector((state) => state.LanguageSlice);
  const [points, setPoints] = useState("");
  const [pointsError, setPointsError] = useState("");
  const [wallet, setWallet] = useState(initialBalance);

  const [getUserWalletBalance, { isLoading: balanceLoading }] =
    useGetUserWalletBalanceMutation();
  const [createUserWalletTransaction, { isLoading: submitLoading }] =
    useCreateUserWalletTransactionMutation();

  const brandId = UTILS.getCurrentUser()?.brandId;
  const userId = customer?._id;

  useEffect(() => {
    if (!open) return;
    setPoints("");
    setPointsError("");
    if (
      initialBalance &&
      (initialBalance.balance != null ||
        initialBalance.lifetimeSpending != null)
    ) {
      setWallet(initialBalance);
      return;
    }
    if (!userId || !brandId) {
      setWallet({ balance: 0, lifetimeSpending: 0 });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await getUserWalletBalance({ userId, brandId });
        if (cancelled) return;
        if (res?.data?.success === 1 && res?.data?.data) {
          setWallet(res.data.data);
        } else {
          setWallet({ balance: 0, lifetimeSpending: 0 });
        }
      } catch {
        if (!cancelled) setWallet({ balance: 0, lifetimeSpending: 0 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, userId, brandId, initialBalance, getUserWalletBalance]);

  const validate = () => {
    const n = Number(String(points).trim());
    if (String(points).trim() === "" || Number.isNaN(n) || n < 1) {
      setPointsError(t("Number of points should be greater than 0"));
      return false;
    }
    setPointsError("");
    return true;
  };

  const handleSubmit = async () => {
    if (!validate() || !userId) return;
    const payload = {
      userId,
      MANUAL_CREDITED: {
        amount: Number(String(points).trim()),
      },
    };
    try {
      const res = await createUserWalletTransaction(payload);
      if (res?.data?.success === 1) {
        setPoints("");
        await onSuccess?.();
        onClose?.();
      }
    } catch {
      /* errors handled in mutation onQueryStarted */
    }
  };

  const balancePoints = Number(wallet?.balance ?? 0);
  const balanceLabel = new Intl.NumberFormat(currentLanguage, {
    maximumFractionDigits: 2,
  }).format(balancePoints);

  const displayPhone = customer
    ? `+${customer.countryCode || ""}${customer.phoneNumber || ""}`
    : "";

  return (
    <AppModal open={!!open} title={t("Add Loyalty Points")} onClose={onClose}>
      {customer?.name ? (
        <div className="mb-4 space-y-1 text-sm text-gray-600">
          <p className="m-0">
            <span className="font-semibold text-gray-900">{t("Customer Name")}: </span>
            {customer.name || t("N/A")}
          </p>
          {displayPhone ? (
            <p className="m-0" dir="ltr">
              <span className="font-semibold text-gray-900">{t("Whatsapp Number")}: </span>
              {displayPhone}
            </p>
          ) : null}
        </div>
      ) : null}

      <AppSimpleText>
        {t("Customer current loyalty points balance", {
          amount: balanceLabel,
        })}
      </AppSimpleText>

      <div className="mt-4">
        <AppInput
          label={t("Number of points")}
          placeholder={t("Cashback value")}
          type="number"
          min={1}
          value={points}
          onChange={(e) => {
            setPoints(e.target.value);
            setPointsError("");
          }}
          error={pointsError}
          mandatory
          disabled={balanceLoading || !userId}
        />
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <AppButton
          type="button"
          variant="outline"
          size="md"
          disabled={submitLoading}
          onClick={onClose}
        >
          {t("Cancel")}
        </AppButton>
        <AppButton
          type="button"
          variant="primary"
          size="md"
          loading={submitLoading}
          disabled={balanceLoading || !userId || submitLoading}
          onClick={handleSubmit}
        >
          {t("Add")}
        </AppButton>
      </div>
    </AppModal>
  );
};

export default CreditLoyaltyPointsModal;
